// Gmail sync, shared by the Tracker's Sync button and Home's auto-sync.
//
// Auto-sync rule: sync when the page is visible, the last sync is over an hour
// old, and no sync is already running. The times live on the user doc
// (users/{uid}.lastSyncAt / syncStartedAt, written by the backend), so every
// page, tab and device shares them. A hidden tab never syncs.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "../firebase";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

/** How old the last sync must be before Home syncs on its own */
export const AUTO_SYNC_AFTER_MS = 60 * 60 * 1000;
/** A sync started this recently (by any tab) is still running; matches the backend's SYNC_LOCK_SECONDS */
const SYNC_LOCK_MS = 10 * 60 * 1000;
/** How often a visible page re-checks the rule (no request unless it's due) */
const CHECK_EVERY_MS = 5 * 60 * 1000;

export type SyncError = "AUTH_REQUIRED" | "SUBSCRIPTION_REQUIRED" | "SESSION_EXPIRED" | "IN_PROGRESS" | "FAILED";
export type SyncResult = { ok: true } | { ok: false; error: SyncError; message?: string };

// --- One sync at a time in this tab -----------------------------------------------

let inFlight: Promise<SyncResult> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Whether this tab is syncing right now (any page). */
export function useSyncing(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => inFlight !== null
  );
}

/** The sync running in this tab, if any, so a page can wait for it. */
export const currentSync = () => inFlight;

/** Run a sync, or join the one already running in this tab. */
export function syncGmail(user: User): Promise<SyncResult> {
  if (inFlight) return inFlight;
  inFlight = requestSync(user).finally(() => {
    inFlight = null;
    notify();
  });
  notify();
  return inFlight;
}

async function requestSync(user: User): Promise<SyncResult> {
  try {
    const idToken = await user.getIdToken();
    const params = new URLSearchParams({
      user_email: user.email || "",
      user_name: user.displayName || "",
      user_photo: user.photoURL || "",
    });
    const r = await fetch(`${API_BASE}/dashboard/tracker?${params}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    const json = await r.json().catch(() => null);
    if (r.status === 401) return { ok: false, error: "SESSION_EXPIRED" };
    if (r.status === 402 || json?.error === "SUBSCRIPTION_REQUIRED") return { ok: false, error: "SUBSCRIPTION_REQUIRED" };
    if (r.status === 409 || json?.error === "SYNC_IN_PROGRESS") return { ok: false, error: "IN_PROGRESS" };
    if (json?.error === "AUTH_REQUIRED") return { ok: false, error: "AUTH_REQUIRED" };
    if (!r.ok) return { ok: false, error: "FAILED", message: json?.error };
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: "FAILED", message: e?.message };
  }
}

// --- Sync times on the user doc ---------------------------------------------------

export type SyncTimes = { lastSyncAt: number | null; syncStartedAt: number | null };

const millis = (v: any): number | null => (v && typeof v.toMillis === "function" ? v.toMillis() : null);

/** The user's sync times, live; null until the first snapshot. */
export function useSyncTimes(user: User | null): SyncTimes | null {
  const [times, setTimes] = useState<SyncTimes | null>(null);
  useEffect(() => {
    setTimes(null);
    if (!user) return;
    return onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        const data = snap.data() || {};
        setTimes({ lastSyncAt: millis(data.lastSyncAt), syncStartedAt: millis(data.syncStartedAt) });
      },
      (error) => console.error("Could not load sync status:", error)
    );
  }, [user]);
  return times;
}

/**
 * Whether to sync now: the last sync (or this tab's last try) is over an hour
 * old, and no tab started one in the last few minutes.
 */
export function autoSyncDue(times: SyncTimes, lastAttempt: number, now = Date.now()): boolean {
  if (times.syncStartedAt !== null && now - times.syncStartedAt < SYNC_LOCK_MS) return false;
  const last = Math.max(times.lastSyncAt ?? 0, lastAttempt);
  return now - last >= AUTO_SYNC_AFTER_MS;
}

/**
 * Sync on open and whenever the page comes back into view, if it's due.
 * Returns the sync times (for "synced 4 min ago") and an error worth showing:
 * Gmail needs reconnecting. Other failures wait for the next due check.
 */
export function useAutoSync(user: User | null): { times: SyncTimes | null; needsReconnect: boolean } {
  const times = useSyncTimes(user);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  // This tab's last try, so a failing sync isn't retried every check
  const lastAttempt = useRef(0);
  // Stop for this visit when retrying can't help
  const stopped = useRef(false);
  const latest = useRef({ user, times });
  latest.current = { user, times };

  const maybeSync = useCallback(() => {
    const { user, times } = latest.current;
    if (!user || !times || stopped.current || inFlight) return;
    if (document.visibilityState !== "visible") return;
    if (!autoSyncDue(times, lastAttempt.current)) return;
    lastAttempt.current = Date.now();
    syncGmail(user).then((result) => {
      if (result.ok || result.error === "IN_PROGRESS") return;
      if (result.error === "AUTH_REQUIRED") setNeedsReconnect(true);
      // The paywall and the login page handle these
      if (result.error !== "FAILED") stopped.current = true;
      else console.error("Auto-sync failed:", result.message);
    });
  }, []);

  useEffect(() => {
    lastAttempt.current = 0;
    stopped.current = false;
    setNeedsReconnect(false);
  }, [user]);

  // On open (once the times load), and when they change
  useEffect(maybeSync, [maybeSync, times]);

  useEffect(() => {
    const onVisible = () => maybeSync();
    document.addEventListener("visibilitychange", onVisible);
    const check = window.setInterval(maybeSync, CHECK_EVERY_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(check);
    };
  }, [maybeSync]);

  return { times, needsReconnect };
}
